'use client'

/**
 * NewsImageMarquee — horizontales Bilder-Laufband oben auf der Frontseite
 * (Stephan-Spec 2026-06-14: "bilder news band oben"). Nur auf "/" gemountet
 * via direktes Einbinden in app/page.tsx.
 *
 * Pulled News mit image_url aus /api/news. Wenn 0 → rendert nichts.
 * Cinematic 60s/Durchlauf (klar lesbar, nicht hektisch). Pause-on-Hover.
 * Klick auf Karte → /news/[slug].
 *
 * Endlos-Loop via Content-Duplikat (klassisches CSS-Marquee-Pattern).
 */

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useTranslation } from '@/i18n/client'

type NewsTranslationFields = {
  title?: string
  excerpt?: string | null
  ticker_text?: string | null
  image_alt?: string | null
}

type NewsItem = {
  id: string
  slug: string
  title: string
  excerpt?: string | null
  category?: string
  image_url: string | null
  image_alt?: string | null
  published_at?: string | null
  // 2026-06-15: Per-Locale-Übersetzungen aus translations_json.
  // Schema: { "fr": { title, excerpt, image_alt, ... }, "en": {...}, ... }
  translations_json?: Record<string, NewsTranslationFields> | null
}

// Mapping unserer UI-Locales auf JS-Date-Locale-Tags. zh-CN bleibt 1:1,
// alle anderen sind direkt BCP-47-kompatibel.
function dateLocaleFor(uiLocale: string): string {
  if (uiLocale === 'zh-CN') return 'zh-CN'
  return uiLocale
}

// Holt den lokalisierten Titel/Excerpt/Alt mit Fallback aufs deutsche Original.
function localized(item: NewsItem, locale: string): { title: string; alt: string } {
  if (locale === 'de') {
    return { title: item.title, alt: item.image_alt || item.title }
  }
  const t = item.translations_json?.[locale]
  return {
    title: (t?.title && t.title.trim()) || item.title,
    alt: (t?.image_alt && t.image_alt.trim()) || (item.image_alt || item.title),
  }
}

export function NewsImageMarquee() {
  const [items, setItems] = useState<NewsItem[] | null>(null)
  const { locale } = useTranslation()

  useEffect(() => {
    let cancelled = false
    fetch('/api/news?limit=20', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (cancelled) return
        const all: NewsItem[] = Array.isArray(j?.entries) ? j.entries : []
        // Nur Eintraege mit Bild — ohne Bild kein Bilderband-Sinn.
        const withImg = all.filter((n) => typeof n.image_url === 'string' && n.image_url.length > 0)
        setItems(withImg)
      })
      .catch(() => { if (!cancelled) setItems([]) })
    return () => { cancelled = true }
  }, [])

  if (!items || items.length === 0) return null

  // Cycle-Dauer skaliert mit Anzahl: 3s pro Karte, mindestens 30s, max 90s.
  const cycleS = Math.max(30, Math.min(90, items.length * 3))
  // Doppelter Content fuer nahtlosen Loop.
  const doubled = [...items, ...items]

  return (
    <div
      className="news-marquee-band relative w-full overflow-hidden"
      style={{
        background:
          'linear-gradient(180deg, color-mix(in oklch, var(--accent) 5%, transparent) 0%, transparent 100%)',
        borderBottom: '1px solid color-mix(in oklch, var(--award-line) 70%, transparent)',
      }}
      aria-label="News-Bilder-Laufband"
    >
      {/* Side-Fades damit Karten am Rand sanft ausblenden */}
      <div className="news-marquee-fade-left" aria-hidden />
      <div className="news-marquee-fade-right" aria-hidden />

      <div
        className="news-marquee-track"
        style={{
          animationDuration: `${cycleS}s`,
        }}
      >
        {doubled.map((item, i) => {
          const loc = localized(item, locale)
          return (
            <Link
              key={`${item.id}-${i}`}
              href={`/news/${item.slug}`}
              className="news-marquee-card group"
              title={loc.title}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={item.image_url || ''}
                alt={loc.alt}
                loading="lazy"
                draggable={false}
              />
              <div className="news-marquee-overlay">
                <div className="news-marquee-title">{loc.title}</div>
                <div className="news-marquee-meta">
                  {item.category && (
                    <span className="news-marquee-cat">{item.category}</span>
                  )}
                  {item.published_at && (
                    <span className="news-marquee-date">
                      {new Date(item.published_at).toLocaleDateString(dateLocaleFor(locale), { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}
